import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { usePeriod } from '@/app/PeriodContext';
import { PeriodBadge } from '@/app/PeriodBadge';
import { formatMoney } from '@/lib/money';
import type { MoneyString } from '@/lib/money';

type AuditRow = {
  id: number;
  record: { type: string; id: number };
  event: string;
  event_label: string;
  module: string;
  entity_id: number | null;
  changed_fields: string[];
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  amount_before: MoneyString | null;
  amount_after: MoneyString | null;
  reason: string | null;
  user: { id: number | null; name: string | null };
  ip_address: string | null;
  route: string | null;
  request_id: string | null;
  created_at: string;
};

const TABLE_LABELS: Record<string, string> = {
  fin_transactions: 'Lançamento',
  fin_transaction_settlements: 'Baixa',
  fin_transfers: 'Transferência',
  fin_accounts: 'Conta',
  fin_credit_cards: 'Cartão',
  fin_loans: 'Dívida',
  fin_budgets: 'Orçamento',
  fin_categories: 'Categoria',
  core_entities: 'Entidade',
};

const EVENT_TONE: Record<string, string> = {
  created: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  updated: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  deleted: 'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300',
  reclassified: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300',
  purged: 'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300',
};

function formatTimestamp(value: string): string {
  const [date, time] = value.replace('T', ' ').split(/[ .]/);
  if (!date) return value;
  const [y, m, d] = date.split('-');
  return `${d}/${m}/${y} ${(time ?? '').slice(0, 5)}`;
}

/**
 * Trilha de auditoria — RF-ADM-03. Somente leitura.
 *
 * A tela mostra o DIFF, nao apenas "alterou". Saber que alguem mexeu num
 * lancamento sem saber o que mudou nao resolve a pergunta que motiva abrir a
 * auditoria.
 */
export function AuditPage({ selectedIds }: { selectedIds: number[] }) {
  const { period } = usePeriod();
  const [page, setPage] = useState(1);
  const [event, setEvent] = useState('');
  const [type, setType] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [expanded, setExpanded] = useState<number | null>(null);

  // `created_at` do log: o recorte e QUANDO A ALTERACAO ACONTECEU, nao a data
  // do lancamento alterado. Um pagamento de janeiro corrigido hoje aparece hoje.
  const params = new URLSearchParams({
    page: String(page),
    per_page: '30',
    from: period.from,
    to: period.to,
  });
  if (event) params.set('event', event);
  if (type) params.set('auditable_type', type);
  if (minAmount) params.set('min_amount', minAmount);

  const logs = useQuery({
    queryKey: ['audit', params.toString(), selectedIds],
    queryFn: () =>
      api.get<{ data: AuditRow[]; meta: { total: number; last_page: number; note: string } }>(
        `/audit?${params.toString()}`,
        { entityIds: selectedIds },
      ),
  });

  const rows = logs.data?.data ?? [];
  const meta = logs.data?.meta;

  return (
    <div className="mx-auto max-w-6xl">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Auditoria</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Quem alterou o quê, quando e de onde — pela data da alteração.{' '}
          {meta ? `${meta.total} registro(s) no período.` : ''}
        </p>
      </div>

      <div className="mt-4 flex flex-wrap items-end gap-2">
        <PeriodBadge />
        <select
          value={type}
          onChange={(e) => { setType(e.target.value); setPage(1); }}
          className="input py-1.5 text-sm"
        >
          <option value="">Todos os registros</option>
          {Object.entries(TABLE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>

        <select
          value={event}
          onChange={(e) => { setEvent(e.target.value); setPage(1); }}
          className="input py-1.5 text-sm"
        >
          <option value="">Todos os eventos</option>
          <option value="created">Criado</option>
          <option value="updated">Alterado</option>
          <option value="deleted">Excluído</option>
          <option value="reclassified">Reclassificado em massa</option>
          <option value="purged">Limpeza de movimento</option>
        </select>

        <input
          value={minAmount}
          onChange={(e) => { setMinAmount(e.target.value.replace(/\D/g, '')); setPage(1); }}
          inputMode="numeric"
          placeholder="Valor mínimo"
          className="input w-36 py-1.5 text-sm"
          title="Alterações que envolveram valor a partir de"
        />
      </div>

      {logs.isPending ? (
        <div className="mt-5 animate-pulse space-y-1.5">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="h-12 rounded bg-slate-200 dark:bg-slate-800" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="mt-8 text-center text-sm text-slate-500 dark:text-slate-400">
          Nenhum registro para os filtros escolhidos.
        </p>
      ) : (
        <div className="mt-4 rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
          {rows.map((row) => (
            <div key={row.id} className="border-b border-slate-100 last:border-0 dark:border-slate-800">
              <button
                onClick={() => setExpanded(expanded === row.id ? null : row.id)}
                className="flex w-full flex-wrap items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800/50"
              >
                <span className="w-32 shrink-0 text-xs text-slate-500 dark:text-slate-400">
                  {formatTimestamp(row.created_at)}
                </span>

                <span
                  className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${
                    EVENT_TONE[row.event] ?? 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                  }`}
                >
                  {row.event_label}
                </span>

                <span className="font-medium">
                  {TABLE_LABELS[row.record.type] ?? row.record.type} #{row.record.id}
                </span>

                {row.changed_fields.length > 0 && row.event === 'updated' && (
                  <span className="truncate text-xs text-slate-500 dark:text-slate-400">
                    {row.changed_fields.slice(0, 3).join(', ')}
                    {row.changed_fields.length > 3 && ` +${row.changed_fields.length - 3}`}
                  </span>
                )}

                {row.amount_after !== null && (
                  <span className="text-xs">
                    {row.amount_before !== null && row.amount_before !== row.amount_after && (
                      <span className="text-slate-400 line-through">
                        {formatMoney(row.amount_before)}
                      </span>
                    )}{' '}
                    <span className="font-medium">{formatMoney(row.amount_after)}</span>
                  </span>
                )}

                <span className="ml-auto text-xs text-slate-500 dark:text-slate-400">
                  {/* Cópia do nome no momento do fato: continua legível depois
                      de o usuário ser excluído. */}
                  {row.user.name ?? 'sistema'}
                </span>
              </button>

              {expanded === row.id && <Diff row={row} />}
            </div>
          ))}
        </div>
      )}

      {meta && meta.last_page > 1 && (
        <div className="mt-3 flex items-center justify-center gap-2 text-sm">
          <button
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
            disabled={page === 1}
            className="rounded-lg border border-slate-300 px-3 py-1.5 disabled:opacity-40 dark:border-slate-700"
          >
            Anterior
          </button>
          <span className="text-slate-500 dark:text-slate-400">
            {page} de {meta.last_page}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(p + 1, meta.last_page))}
            disabled={page >= meta.last_page}
            className="rounded-lg border border-slate-300 px-3 py-1.5 disabled:opacity-40 dark:border-slate-700"
          >
            Próxima
          </button>
        </div>
      )}

      {meta && <p className="mt-3 text-[11px] text-slate-400">{meta.note}</p>}
    </div>
  );
}

// -------------------------------------------------------------------

function Diff({ row }: { row: AuditRow }) {
  const fields = row.changed_fields.length > 0
    ? row.changed_fields
    : Object.keys(row.new_values ?? {});

  return (
    <div className="border-t border-slate-100 bg-slate-50 px-3 py-3 text-xs dark:border-slate-800 dark:bg-slate-800/40">
      {row.reason && (
        <p className="mb-2 text-slate-600 dark:text-slate-300">
          <strong>Motivo:</strong> {row.reason}
        </p>
      )}

      {fields.length === 0 ? (
        <p className="text-slate-500 dark:text-slate-400">Sem campos registrados.</p>
      ) : (
        <table className="w-full">
          <thead className="text-left text-[10px] uppercase tracking-wide text-slate-400">
            <tr>
              <th className="pb-1 font-medium">Campo</th>
              <th className="pb-1 font-medium">Antes</th>
              <th className="pb-1 font-medium">Depois</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field) => (
              <tr key={field} className="align-top">
                <td className="py-0.5 pr-3 font-medium">{field}</td>
                <td className="py-0.5 pr-3 text-rose-700 dark:text-rose-400">
                  {renderValue(row.old_values?.[field])}
                </td>
                <td className="py-0.5 text-emerald-700 dark:text-emerald-400">
                  {renderValue(row.new_values?.[field])}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p className="mt-2 text-[10px] text-slate-400">
        {row.ip_address && `IP ${row.ip_address} · `}
        {row.route && `${row.route} · `}
        ação {row.request_id?.slice(0, 8)}
      </p>
    </div>
  );
}

function renderValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'sim' : 'não';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}
